import type { DataRow } from './types';
import { roleLabels, userStatusLabels, valueLabel } from './labels';
import { formatPrice } from '../../core/formatPrice';

const changeFields = ['name', 'provider', 'enabled', 'status', 'role', 'amountCents', 'modelId', 'upstreamId', 'weight', 'isPublished', 'inputPerMillion', 'outputPerMillion', 'cachedInputPerMillion', 'effectiveFrom', 'showContextInjection', 'showReasoning'];
const paymentFields = ['outTradeNo', 'tradeNo', 'receivedMoney', 'expectedCents'];
const fieldLabels: Record<string, string> = {
  showContextInjection: '对话显示已注入提示', showReasoning: '对话显示思考过程',
  name: '名称', provider: '提供商', enabled: '启用', status: '状态', role: '角色', amountCents: '金额（分）',
  modelId: '模型 ID', upstreamId: '渠道 ID', weight: '权重', isPublished: '已发布',
  inputPerMillion: '输入价格 / 百万 token', outputPerMillion: '输出价格 / 百万 token', cachedInputPerMillion: '缓存输入价格 / 百万 token',
  effectiveFrom: '生效时间', outTradeNo: '商户订单号', tradeNo: '平台流水号', receivedMoney: '实收金额', expectedCents: '应付（分）',
};
const fieldValues: Record<string, Record<string, string>> = { role: roleLabels, status: userStatusLabels };

function displayValue(key: string, raw: unknown): string {
  if (typeof raw === 'boolean') return raw ? '是' : '否';
  if (['inputPerMillion', 'outputPerMillion', 'cachedInputPerMillion'].includes(key)) return formatPrice(typeof raw === 'string' || typeof raw === 'number' ? raw : 0);
  const labels = Object.hasOwn(fieldValues, key) ? fieldValues[key] : undefined;
  return labels && typeof raw === 'string' ? valueLabel(labels, raw) : String(raw);
}

const trustedPaymentReasons = new Set([
  'INVALID_TRADE_IDENTIFIERS', 'ORDER_NOT_FOUND', 'INVALID_PAYMENT_AMOUNT', 'AMOUNT_MISMATCH',
  'TRADE_NO_CONFLICT', 'PAID_ORDER_REQUIRES_RECONCILIATION', 'PAYMENT_EVENT_REQUIRES_RECONCILIATION',
  'ORDER_NOT_PENDING', 'ORDER_EXPIRED', 'INVALID_SIGNATURE', 'MERCHANT_MISMATCH',
  'UNSUPPORTED_TRADE_STATUS', 'PAYMENT_NOT_CONFIGURED', 'INVALID_CALLBACK_BODY',
]);
const paymentReasonLabels: Record<string, string> = {
  INVALID_TRADE_IDENTIFIERS: '交易标识无效',
  ORDER_NOT_FOUND: '订单不存在',
  INVALID_PAYMENT_AMOUNT: '支付金额无效',
  AMOUNT_MISMATCH: '支付金额不匹配',
  TRADE_NO_CONFLICT: '平台流水号冲突',
  PAID_ORDER_REQUIRES_RECONCILIATION: '已支付订单需人工核对',
  PAYMENT_EVENT_REQUIRES_RECONCILIATION: '支付事件需人工核对',
  ORDER_NOT_PENDING: '订单不是待支付状态',
  ORDER_EXPIRED: '订单已过期',
  INVALID_SIGNATURE: '签名校验失败',
  MERCHANT_MISMATCH: '商户号不匹配',
  UNSUPPORTED_TRADE_STATUS: '不支持的交易状态',
  PAYMENT_NOT_CONFIGURED: '支付服务未配置',
  INVALID_CALLBACK_BODY: '支付回调格式无效',
};

function metadata(row: DataRow): Record<string, unknown> {
  return row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata) ? row.metadata as Record<string, unknown> : {};
}

export function paymentRejectionReason(row: DataRow): string {
  const reason = metadata(row).reason;
  if (row.action !== 'payment.notify.rejected' || typeof reason !== 'string' || !trustedPaymentReasons.has(reason)) return '-';
  const label = Object.hasOwn(paymentReasonLabels, reason) ? paymentReasonLabels[reason] : undefined;
  // The audit endpoint supplies trusted reasons. Keep established reason codes,
  // and make the additional public reasons readable and searchable in Chinese.
  return label ? `${label}（${reason}）` : reason;
}

export function auditSummary(row: DataRow): string {
  const value = metadata(row);
  const fields = row.action === 'payment.notify.rejected' ? paymentFields : changeFields;
  return fields.filter((key) => typeof value[key] === 'boolean' || typeof value[key] === 'number' || typeof value[key] === 'string')
    .map((key) => `${fieldLabels[key] ?? key}：${displayValue(key, value[key])}`).join('；') || '-';
}
